import React, { useState, useEffect, useRef } from 'react';
import { useNewUI } from '../hooks/useNewUI';
import { cn } from '../utils/classNames';
import { Button, Badge } from '../components/ui';
import { useParams, Link } from 'react-router-dom';
import {
Battery, ChevronLeft, Clock, Zap, Scale, Shield, ArrowLeft, Gauge, PenTool as Tool,
Wifi, Smartphone, Disc, Ruler, Sun, Cpu, Award, Share2, Heart, Download, ArrowRight,
CheckCircle2, Gift, FileCheck, FileText, HardHat, ChevronRight, Star, Tag, X, ChevronDown, ChevronUp,
Info, Truck, MapPin, Calendar, CreditCard, Percent, Check, MessageSquare, Play, Pause
} from 'lucide-react';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import { MOTORCYCLE_MODELS } from '../constants/products';
import { formatCurrency } from '../utils/currency';
import PayMoloForm from '../components/forms/PayMoloForm';
import SimulationForm from '../components/forms/SimulationForm';
import TerangaPlusReservationForm from '../components/forms/TerangaPlusReservationForm';

const ProductDetail: React.FC = () => {
const isNewUI = useNewUI();
const { slug } = useParams<{ slug: string }>();
const [product, setProduct] = useState<any | null>(null);
const [loading, setLoading] = useState(true);
const [selectedImage, setSelectedImage] = useState<string | null>(null);
const [isLiked, setIsLiked] = useState(false);
const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'features' | 'docs'>('overview');
const [zoomedImage, setZoomedImage] = useState<boolean>(false);
const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
const [showPayMoloForm, setShowPayMoloForm] = useState(false);
const [showSimulationForm, setShowSimulationForm] = useState(false);
const [showTerangaPlusReservation, setShowTerangaPlusReservation] = useState(false);
const [showFullscreenGallery, setShowFullscreenGallery] = useState(false);
const [galleryIndex, setGalleryIndex] = useState(0);
const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
const [isVideoPlaying, setIsVideoPlaying] = useState(false);
const videoRef = useRef<HTMLVideoElement>(null);
const [showComparisonTable, setShowComparisonTable] = useState(false);
const [selectedColor, setSelectedColor] = useState<string>('Noir');

useEffect(() => {
try {
if (!slug) {
setProduct(null);
setLoading(false);
return;
}

const foundProduct = MOTORCYCLE_MODELS.find(model => model.id === slug);

if (foundProduct) {
const isTerangaPlus = foundProduct.id === 'teranga-plus';

setProduct({
...foundProduct,
specifications: {
autonomie: foundProduct.specs.autonomie,
vitesse_max: foundProduct.specs.vitesse,
poids: foundProduct.specs.poids,
puissance: isTerangaPlus ? '3000W' : '3000W',
batterie: isTerangaPlus ? '72V 30Ah Lithium-ion' : '72V 30Ah Lithium-ion',
temps_charge: isTerangaPlus ? '4-6 heures' : '4-6 heures',
cout_recharge: isTerangaPlus ? 'Moins de 300 FCFA' : undefined,
energie: isTerangaPlus ? '100% électrique - Zéro pollution' : undefined,
suspension: 'Fourche hydraulique avant, Double amortisseur arrière',
freins: 'Disque hydraulique avant et arrière',
pneus: 'Tubeless tout-terrain 90/90-14',
éclairage: 'Full LED avec feux de jour',
affichage: 'Écran LCD couleur',
connectivité: 'Bluetooth, Application mobile',
avantages: isTerangaPlus ? [
"Idéal pour familles, particuliers et étudiants",
"Moteur électrique puissant et silencieux",
"Coûts d'exploitation ultra-réduits",
"Design moderne et confortable",
"100% électrique - Zéro émission"
] : [
"Premier mois gratuit avec 7 Swaps inclus",
"Protection complète 12 mois (RC + Tous Risques)",
"Garantie constructeur 12 mois",
"Carte grise & plaque d'immatriculation incluses",
"Kit de sécurité premium offert (casque + gilet)"
]
}
});
if (foundProduct.image) {
setSelectedImage(foundProduct.image);
}
} else {
setProduct(null);
}
} catch (error) {
console.error('Error loading product:', error);
setProduct(null);
} finally {
setLoading(false);
}
}, [slug]);

useEffect(() => {
if (product) {
document.title = `${product.name} | Proxy Emobility`;
}
}, [product]);

useEffect(() => {
if (product && product.id === 'teranga-plus') {
if (selectedColor === 'Blanc' && product.imageWhite) {
setSelectedImage(product.imageWhite);
} else if (selectedColor === 'Noir' && product.imageBlack) {
setSelectedImage(product.imageBlack);
}
}
}, [selectedColor, product]);

const handleShare = () => {
const url = window.location.href;
if (navigator.share) {
navigator.share({
title: product?.name || '',
text: product?.description || '',
url: url
});
} else {
navigator.clipboard.writeText(url);
toast.success('Lien copié !');
}
};

const handleLike = () => {
setIsLiked(!isLiked);
toast.success(isLiked ? 'Retiré des favoris' : 'Ajouté aux favoris');
};

const handleDownloadSpec = () => {
if (!product) return;

const doc = new jsPDF();

// Add logo and header
doc.setFontSize(20);
doc.setTextColor(24, 153, 78); // Primary color
doc.text('Proxy Emobility', 20, 20);

// Product name and model
doc.setFontSize(20);
doc.setTextColor(0, 0, 0);
doc.text(product.name, 20, 40);

// Description
doc.setFontSize(12);
doc.setTextColor(60, 60, 60);
const descriptionLines = doc.splitTextToSize(product.description, 170);
doc.text(descriptionLines, 20, 70);

// Specifications
doc.setFontSize(16);
doc.setTextColor(0, 0, 0);
doc.text('Caractéristiques Techniques', 20, 100);

const specs = product.specifications;
let yPos = 120;

// Group specifications
const specGroups = {
'Performance': ['autonomie', 'puissance', 'vitesse_max', 'temps_charge'],
'Caractéristiques': ['poids', 'charge_max', 'batterie'],
'Équipement': ['suspension', 'freins', 'pneus', 'éclairage'],
'Connectivité': ['affichage', 'connectivité']
};

Object.entries(specGroups).forEach(([group, keys]) => {
// Add group title
doc.setFontSize(14);
doc.setTextColor(24, 153, 78);
doc.text(group, 20, yPos);
yPos += 10;

// Add specifications
doc.setFontSize(12);
doc.setTextColor(60, 60, 60);
keys.forEach(key => {
if (specs[key]) {
doc.text(`${key.replace('_', ' ').toUpperCase()}: ${specs[key]}`, 30, yPos);
yPos += 8;
}
});
yPos += 5;

// Add new page if needed
if (yPos > 270) {
doc.addPage();
yPos = 20;
}
});

// Add footer
const pageCount = doc.getNumberOfPages();
doc.setFontSize(10);
doc.setTextColor(150, 150, 150);
for (let i = 1; i <= pageCount; i++) {
doc.setPage(i);
doc.text(
`${product.name} - Spécifications Techniques - Page ${i} sur ${pageCount}`,
20,
290
);
doc.text('www.proxy-emobility.com', 150, 290);
}

// Save the PDF
doc.save(`${product.name.toLowerCase().replace(/ /g, '-')}-specifications.pdf`);
toast.success('Spécifications téléchargées en PDF');
};

const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
if (!zoomedImage) return;

const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
const x = (e.clientX - left) / width;
const y = (e.clientY - top) / height;

setMousePosition({ x, y });
};

const handleThumbnailClick = (image: string) => {
setSelectedImage(image);
setZoomedImage(false);
};

const handleGalleryOpen = (index: number) => {
setGalleryIndex(index);
setShowFullscreenGallery(true);
document.body.style.overflow = 'hidden';
};

const handleGalleryClose = () => {
setShowFullscreenGallery(false);
document.body.style.overflow = 'auto';
};

const handleGalleryNext = () => {
if (!product) return;
setGalleryIndex((prevIndex) => (prevIndex + 1) % productImages.length);
};

const handleGalleryPrev = () => {
if (!product) return;
setGalleryIndex((prevIndex) => (prevIndex - 1 + productImages.length) % productImages.length);
};

const toggleFaq = (index: number) => {
setExpandedFaq(expandedFaq === index ? null : index);
};

const toggleVideo = () => {
if (videoRef.current) {
if (isVideoPlaying) {
videoRef.current.pause();
} else {
videoRef.current.play();
}
setIsVideoPlaying(!isVideoPlaying);
}
};

useEffect(() => {
const handleKeyDown = (e: KeyboardEvent) => {
if (!showFullscreenGallery) return;

if (e.key === 'Escape') {
handleGalleryClose();
} else if (e.key === 'ArrowRight') {
handleGalleryNext();
} else if (e.key === 'ArrowLeft') {
handleGalleryPrev();
}
};

window.addEventListener('keydown', handleKeyDown);
return () => window.removeEventListener('keydown', handleKeyDown);
}, [showFullscreenGallery]);

if (loading) {
return ( <div className="min-h-screen flex items-center justify-center pt-20"> <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-primary-600"></div> </div>
);
}

if (!product) {
return ( <div className="min-h-screen bg-gray-50 pt-20"> <div className="container mx-auto px-4 py-8 sm:py-12"> <div className="max-w-xl mx-auto text-center"> <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Produit non trouvé</h1> <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
Le produit que vous recherchez n'existe pas ou a été supprimé. </p> <Link 
to="/products" 
className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
> <ArrowLeft size={18} className="mr-2" />
Retour aux produits </Link> </div> </div> </div>
);
}

const specs = product.specifications;

const renderSpecIcon = (spec: string) => {
switch (spec.toLowerCase()) {
case 'autonomie':
return <Battery className="text-primary-500" size={20} />;
case 'vitesse_max':
return <Gauge className="text-primary-500" size={20} />;
case 'poids':
return <Scale className="text-primary-500" size={20} />;
case 'puissance':
return <Zap className="text-primary-500" size={20} />;
case 'temps_charge':
return <Clock className="text-primary-500" size={20} />;
case 'suspension':
return <Tool className="text-primary-500" size={20} />;
case 'connectivité':
return <Wifi className="text-primary-500" size={20} />;
case 'affichage':
return <Smartphone className="text-primary-500" size={20} />;
case 'freins':
return <Disc className="text-primary-500" size={20} />;
case 'pneus':
return <Ruler className="text-primary-500" size={20} />;
case 'éclairage':
return <Sun className="text-primary-500" size={20} />;
case 'batterie':
return <Cpu className="text-primary-500" size={20} />;
default:
return <Award className="text-primary-500" size={20} />;
}
};

const mainSpecs = ['autonomie', 'vitesse_max', 'poids', 'temps_charge'];
const technicalSpecs = ['puissance', 'batterie', 'suspension', 'freins'];
const comfortSpecs = ['pneus', 'éclairage', 'affichage', 'connectivité'];

// Get product images
const productImages = product.image ? [product.image] : [];
if (product.images && product.images.length > 0) {
productImages.length = 0; // Clear the array
productImages.push(...product.images);
}

// FAQ data
const faqData = [
{
question: "Quelle est l'autonomie réelle de la moto ?",
answer: `L'autonomie de la ${product.name} est de ${specs.autonomie} en conditions normales d'utilisation. Cette autonomie peut varier en fonction du poids du conducteur, du type de terrain et des conditions météorologiques.`
},
{
question: "Comment fonctionne le système de recharge ?",
answer: `La ${product.name} peut être rechargée de deux façons : via une prise standard en 4-6 heures ou dans nos stations Proxy Power en 2-3 heures. Vous pouvez également opter pour notre service d'échange de batterie pour une solution instantanée.`
},
{
question: "Quelles sont les options de financement disponibles ?",
answer: "Nous proposons plusieurs options de financement : achat comptant, Pay-as-you-go (paiement à l'utilisation) et Pay Molo (paiement avant livraison). Chaque option offre des avantages différents selon vos besoins."
},
{
question: "La moto est-elle adaptée aux routes africaines ?",
answer: `Absolument ! La ${product.name} a été spécialement conçue pour les conditions routières africaines avec une suspension renforcée, des pneus adaptés et une structure robuste pour résister aux routes difficiles.`
},
{
question: "Quelle est la durée de vie de la batterie ?",
answer: "Nos batteries ont une durée de vie de plus de 1500 cycles de charge, ce qui représente environ 5 ans d'utilisation normale. Après cette période, la batterie conserve encore 80% de sa capacité initiale."
}
];

// Comparison data
const comparisonData = {
models: [
{ name: product.name, price: product.basePrice },
...MOTORCYCLE_MODELS.filter(m => m.id !== product.id).map(m => ({ name: m.name, price: m.basePrice }))
],
features: [
{ name: "Autonomie", values: [specs.autonomie, "100 km", "80 km"] },
{ name: "Vitesse max", values: [specs.vitesse_max, "45 km/h", "40 km/h"] },
{ name: "Puissance", values: [specs.puissance, "4000W", "2500W"] },
{ name: "Poids", values: [specs.poids, "85 kg", "75 kg"] },
{ name: "Temps de charge", values: [specs.temps_charge, "4-6 heures", "3-5 heures"] },
{ name: "Connectivité", values: ["Application mobile", "Application mobile", "Non"] },
{ name: "Garantie", values: ["12 mois", "12 mois", "12 mois"] }
]
};

return ( <div className="min-h-screen bg-gray-50 pt-20">
{/* Bandeau Prévente */}
{product.id === 'teranga-plus' ? (
<div className="bg-gradient-to-r from-purple-600 to-purple-700 border-b-4 border-purple-400">
<div className="container mx-auto px-4 py-3 sm:py-4">
<div className="flex items-center justify-center gap-2 text-white animate-shimmer">
<Zap size={20} className="animate-pulse" />
<p className="text-sm sm:text-base font-semibold text-center">
🎉 OFFRE SPÉCIALE PRÉVENTE – Réservez avant le <span className="text-purple-200 font-bold">15 Février 2026</span> • Réservation dès <span className="text-purple-200 font-bold">50 000 FCFA via Wave</span>
</p>
</div>
</div>
</div>
) : (
<div className="bg-gradient-to-r from-[#00A859] to-[#0E9F6E] border-b-4 border-accent-300">
<div className="container mx-auto px-4 py-3 sm:py-4">
<div className="flex items-center justify-center gap-2 text-white animate-shimmer">
<Zap size={20} className="animate-pulse" />
<p className="text-sm sm:text-base font-semibold text-center">
🎉 Ce modèle est en PRÉVENTE – Réservez avant le <span className="text-accent-300 font-bold">15 Décembre 2025</span> et bénéficiez d'un mois gratuit + 5 recharges incluses.
</p>
</div>
</div>
</div>
)}

<div className="container mx-auto px-4 py-6 sm:py-12"> <div className="mb-4 sm:mb-8"> <Link
to="/products"
className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors text-sm sm:text-base"
> <ArrowLeft size={18} className="mr-2" />
Retour </Link> </div>

{/* Hero Section */}
<div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
{/* Left Column - Product Images */}
<div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
{/* Main Product Image */}
<div
className="relative h-96 lg:h-[500px] bg-gradient-to-br from-gray-50 via-white to-primary-50 rounded-lg sm:rounded-xl overflow-hidden cursor-zoom-in"
onMouseMove={handleImageMouseMove}
onMouseEnter={() => setZoomedImage(true)}
onMouseLeave={() => setZoomedImage(false)}
onClick={() => handleGalleryOpen(productImages.indexOf(selectedImage || productImages[0]))}
>
<img
src={selectedImage || productImages[0]}
alt={`Moto électrique ${product.name}${product.id === 'teranga-plus' ? ` couleur ${selectedColor}` : ''} - ${product.description}`}
width="800"
height="600"
className="w-full h-full object-contain transition-transform duration-500 p-6"
style={zoomedImage ? {
transform: 'scale(1.5)',
transformOrigin: `${mousePosition.x * 100}% ${mousePosition.y * 100}%`
} : {}}
loading="eager"
onError={(e) => {
const target = e.target as HTMLImageElement;
target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22800%22 height=%22600%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22800%22 height=%22600%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 font-family=%22Arial%22 font-size=%2224%22 fill=%22%239ca3af%22 text-anchor=%22middle%22 dy=%22.3em%22%3EImage non disponible%3C/text%3E%3C/svg%3E';
}}
/>

{/* Gradient overlay pour meilleure visibilité */}
<div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/5 pointer-events-none"></div>

{/* Zoom hint */}
<div className="absolute bottom-3 sm:bottom-4 right-3 sm:right-4 bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs text-gray-600">
Cliquez pour agrandir
</div>
</div>

{/* Thumbnail Gallery */}
{productImages.length > 1 && (
<div className="grid grid-cols-5 gap-2">
{productImages.map((image, index) => (
<div
key={index}
className={cn(
"relative h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all",
selectedImage === image
? 'border-primary-500 ring-2 ring-primary-200 scale-105'
: 'border-gray-200 hover:border-primary-300'
)}
onClick={() => handleThumbnailClick(image)}
>
<img
src={image}
alt={`${product.name} - Vue ${index + 1}`}
width="100"
height="100"
className="w-full h-full object-cover"
loading="lazy"
onError={(e) => {
const target = e.target as HTMLImageElement;
target.style.display = 'none';
}}
/>
</div>
))}
{/* Video thumbnail */}
<div 
className="aspect-w-1 aspect-h-1 rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary-200 bg-gray-100 flex items-center justify-center relative"
onClick={() => document.getElementById('product-video')?.scrollIntoView({ behavior: 'smooth' })}
>
<Play size={20} className="text-primary-600" />
<div className="absolute inset-0 bg-black/5"></div>
</div>
</div>
)}

{/* Quick Specs */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
{mainSpecs.map(key => (
specs[key] && (
<div 
key={key}
className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center group hover:bg-primary-50 transition-colors transform hover:scale-105"
>
<div className="mb-1 sm:mb-2 transform group-hover:scale-110 transition-transform">
{renderSpecIcon(key)}
</div>
<span className="block text-sm sm:text-base font-medium text-gray-900">{specs[key]}</span>
<span className="block text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</span>
</div>
)
))}
</div>
</div>

{/* Right Column - Product Info */}
<div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 bg-gradient-to-br from-gray-50 to-white">
<div>
<div className="flex justify-between items-start">
<div>
<div className="flex items-center gap-2 mb-2">
<span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
{product.usage}
</span>
<div className="flex">
{[1, 2, 3, 4, 5].map((star) => (
<Star 
key={star} 
size={16} 
className={star <= product.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
/>
))}
<span className="ml-1 text-xs text-gray-600">({product.reviews})</span>
</div>
</div>
<h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
<p className="text-sm sm:text-base text-gray-600 leading-relaxed">{product.description}</p>
</div>
<div className="flex gap-2">
<button
onClick={handleLike}
className={`p-2 rounded-lg transition-colors ${
isLiked 
? 'bg-red-100 text-red-500' 
: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
}`}
aria-label={isLiked ? "Retirer des favoris" : "Ajouter aux favoris"}
>
<Heart size={20} className={isLiked ? 'fill-current' : ''} />
</button>
<button
onClick={handleShare}
className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
aria-label="Partager"
>
<Share2 size={20} />
</button>
</div>
</div>
</div>

{/* Color Selector for Teranga+ */}
{product.id === 'teranga-plus' && product.colors && (
<div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
<h3 className="text-sm font-semibold text-gray-900 mb-3">Choisissez votre couleur :</h3>
<div className="flex gap-3">
{product.colors.map((color: string) => (
<button
key={color}
onClick={() => setSelectedColor(color)}
className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
selectedColor === color
? 'border-purple-600 bg-purple-100 shadow-md scale-105'
: 'border-gray-300 bg-white hover:border-purple-400'
}`}
>
<div
className={`w-6 h-6 rounded-full border-2 ${
color === 'Noir'
? 'bg-black border-gray-700'
: 'bg-white border-gray-400'
}`}
></div>
<span className="font-medium text-gray-900">{color}</span>
{selectedColor === color && (
<Check size={16} className="text-purple-600" />
)}
</button>
))}
</div>
<p className="text-xs text-gray-600 mt-2">
Couleur sélectionnée : <strong className="text-purple-700">{selectedColor}</strong>
</p>
</div>
)}

{/* Price and Financing */}
{product.id === 'teranga-plus' ? (
<div className="bg-gradient-to-r from-purple-50 to-white p-4 sm:p-6 rounded-xl border border-purple-200">
<h3 className="text-lg font-bold text-gray-900 mb-4">💰 Prix selon l'option choisie</h3>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
{/* Option Essentiel */}
<div className="bg-white border-2 border-gray-300 rounded-xl p-4 hover:border-purple-400 transition-all">
<div className="flex items-start justify-between mb-3">
<div>
<h4 className="font-bold text-gray-900">Essentiel</h4>
<div className="text-2xl font-bold text-purple-600 mt-1">
{formatCurrency(850000)}
</div>
</div>
</div>
<div className="space-y-2 text-sm text-gray-600 mb-3">
<div className="flex items-center">
<X size={14} className="text-red-500 mr-2" />
Sans carte grise
</div>
<div className="flex items-center">
<X size={14} className="text-red-500 mr-2" />
Sans plaque d'immatriculation
</div>
</div>
<div className="bg-purple-100 border border-purple-300 rounded-lg p-2 text-xs">
<p className="text-purple-900 font-medium">🎁 Cadeau : Assurance maladies 12 mois</p>
</div>
</div>

{/* Option Complet */}
<div className="bg-purple-50 border-2 border-purple-600 rounded-xl p-4 relative shadow-md">
<div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
RECOMMANDÉ
</div>
<div className="flex items-start justify-between mb-3">
<div>
<h4 className="font-bold text-gray-900">Complet</h4>
<div className="text-2xl font-bold text-purple-600 mt-1">
{formatCurrency(950000)}
</div>
</div>
</div>
<div className="space-y-2 text-sm text-gray-700">
<div className="flex items-center">
<Check size={14} className="text-green-500 mr-2" />
Carte grise
</div>
<div className="flex items-center">
<Check size={14} className="text-green-500 mr-2" />
Plaque d'immatriculation
</div>
<div className="flex items-center">
<Check size={14} className="text-green-500 mr-2" />
Assurance RC
</div>
<div className="flex items-center">
<Check size={14} className="text-green-500 mr-2" />
Assurance Accident 12 mois
</div>
<div className="flex items-center">
<Check size={14} className="text-green-500 mr-2" />
Assurance Maladie 12 mois
</div>
</div>
</div>
</div>

<button
onClick={() => setShowTerangaPlusReservation(true)}
className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg font-bold text-lg"
>
<Calendar size={20} />
🚀 Réserver Teranga+
</button>
</div>
) : (
<div className="bg-gradient-to-r from-primary-50 to-white p-4 sm:p-6 rounded-xl border border-primary-100">
<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
<div>
<span className="text-2xl sm:text-3xl font-bold text-primary-600">
{formatCurrency(product.basePrice)}
</span>
<div className="flex items-center gap-2 mt-1">
<span className="text-xs sm:text-sm text-gray-500">
ou à partir de {formatCurrency(Math.round(product.basePrice / 24))}/mois
</span>
<Info
size={14}
className="text-gray-400 cursor-help"
onMouseOver={() => toast.success('Financement sur 24 mois')}
/>
</div>
</div>
<div className="flex items-center gap-2">
<span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center">
<Check size={12} className="mr-1" />
En stock
</span>
<span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center">
<Truck size={12} className="mr-1" />
Livraison 24-48h
</span>
</div>
</div>

<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
<button
onClick={() => setShowPayMoloForm(true)}
className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors shadow-sm"
>
<CreditCard size={18} />
<div className="text-left">
<span className="block font-medium">Pay Molo</span>
<span className="text-xs text-gray-500">Paiement avant livraison</span>
</div>
</button>
<button
onClick={() => setShowSimulationForm(true)}
className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
>
<Calendar size={18} />
<div className="text-left">
<span className="block font-medium">Pay As You Go</span>
<span className="text-xs text-white/80">Paiement à l'utilisation</span>
</div>
</button>
</div>
</div>
)}

{/* Encadré Offre Prévente */}
{product.id === 'teranga-plus' ? (
<div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-600 rounded-xl p-4 sm:p-6 shadow-lg">
<div className="flex items-start gap-3 mb-4">
<div className="p-2 bg-purple-600 rounded-lg">
<Zap size={24} className="text-white" />
</div>
<div>
<h3 className="text-lg sm:text-xl font-bold text-purple-600 mb-1">💥 Offre Spéciale de Prévente</h3>
<p className="text-sm text-gray-600">Valable jusqu'au <strong className="text-purple-700">15 Février 2026</strong></p>
</div>
</div>
<div className="space-y-3 mb-4">
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-purple-600 flex-shrink-0" />
<span className="text-sm sm:text-base"><strong className="text-purple-600">Réservation dès 50 000 FCFA via Wave</strong></span>
</div>
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-purple-600 flex-shrink-0" />
<span className="text-sm sm:text-base"><strong className="text-purple-600">Livraison prioritaire</strong> dès la fin de la prévente</span>
</div>
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-purple-600 flex-shrink-0" />
<span className="text-sm sm:text-base">2 couleurs disponibles : <strong className="text-purple-600">Noir & Blanc</strong></span>
</div>
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
<button
onClick={() => setShowTerangaPlusReservation(true)}
className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-lg hover:shadow-xl transition-all transform hover:scale-105 group"
>
🚀 Réserver maintenant (Prévente)
<ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
</button>
<button
onClick={() => window.location.href = '/contact'}
className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 font-bold rounded-lg hover:bg-purple-600 hover:text-white transition-all"
>
💬 Contacter un conseiller
</button>
</div>
</div>
) : (
<div className="bg-gradient-to-br from-[#F8FAF9] to-white border-2 border-[#00A859] rounded-xl p-4 sm:p-6 shadow-lg">
<div className="flex items-start gap-3 mb-4">
<div className="p-2 bg-[#00A859] rounded-lg">
<Zap size={24} className="text-white" />
</div>
<div>
<h3 className="text-lg sm:text-xl font-bold text-[#00A859] mb-1">💥 Offre Prévente Spéciale</h3>
<p className="text-sm text-gray-600">Valable uniquement jusqu'au 15 Décembre 2025</p>
</div>
</div>
<div className="space-y-3 mb-4">
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-[#00A859] flex-shrink-0" />
<span className="text-sm sm:text-base">Réduction jusqu'à <strong className="text-[#00A859]">-10%</strong></span>
</div>
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-[#00A859] flex-shrink-0" />
<span className="text-sm sm:text-base"><strong className="text-[#00A859]">1er mois gratuit + 5 recharges incluses</strong></span>
</div>
<div className="flex items-center gap-2 text-gray-800">
<Check size={18} className="text-[#00A859] flex-shrink-0" />
<span className="text-sm sm:text-base"><strong className="text-[#00A859]">Livraison prioritaire</strong> dès la fin de la prévente</span>
</div>
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
<button
onClick={() => setShowPayMoloForm(true)}
className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-[#00A859] to-[#0E9F6E] text-white font-bold rounded-lg hover:shadow-xl transition-all transform hover:scale-105 group"
>
🚀 Réserver maintenant (Prévente)
<ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
</button>
<button
onClick={() => window.location.href = '/contact'}
className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white border-2 border-[#00A859] text-[#00A859] font-bold rounded-lg hover:bg-[#00A859] hover:text-white transition-all"
>
💬 Contacter un conseiller
</button>
</div>
<div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200">
<div className="flex items-center gap-2 text-sm">
<Zap size={16} className="text-[#00A859]" />
<span className="text-gray-700">Déjà <strong className="text-[#00A859]">124 réservations</strong> confirmées</span>
</div>
<div className="w-full sm:w-auto">
<div className="flex items-center gap-2 mb-1">
<span className="text-xs text-gray-600">Stocks réservés</span>
<span className="text-xs font-bold text-[#00A859]">62%</span>
</div>
<div className="w-full sm:w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
<div className="h-full bg-gradient-to-r from-[#00A859] to-[#0E9F6E] rounded-full" style={{ width: '62%' }}></div>
</div>
</div>
</div>
</div>
)}

{/* Info Paiement Flexible - Masqué pour Teranga+ */}
{product.id !== 'teranga-plus' && (
<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
<Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
<p className="text-sm text-blue-900">
<strong>💡 Paiement flexible :</strong> choisissez entre Pay Molo (paiement avant livraison) ou Pay As You Go (paiement à l'utilisation).
</p>
</div>
)}

{/* Key Benefits */}
<div className="space-y-3">
<h3 className="text-lg font-semibold text-gray-900 flex items-center">
<CheckCircle2 size={18} className="text-primary-500 mr-2" />
Avantages inclus
</h3>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
{(specs.avantages as string[])?.slice(0, 4).map((advantage, index) => (
<div 
key={index}
className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-primary-50 transition-all duration-300"
>
<div className="mr-2 mt-0.5">
{advantage.startsWith('🎁') ? <Gift size={16} className="text-primary-500" /> :
advantage.startsWith('🛡️') ? <Shield size={16} className="text-blue-500" /> :
advantage.startsWith('✨') ? <Award size={16} className="text-orange-500" /> :
advantage.startsWith('📝') ? <FileCheck size={16} className="text-purple-500" /> :
<HardHat size={16} className="text-red-500" />}
</div>
<p className="text-gray-700 text-sm">{advantage}</p>
</div>
))}
</div>
</div>

{/* Call to Action */}
<div className="flex flex-col sm:flex-row gap-3">
<Link 
to="/contact"
className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
>
<MessageSquare size={18} className="mr-2" />
Contacter un conseiller
</Link>
<Link 
to="/contact?type=trial"
className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-accent-300 text-primary-900 rounded-lg hover:bg-accent-400 transition-colors shadow-sm"
>
<Calendar size={18} className="mr-2" />
Réserver un essai
</Link>
</div>
</div>
</div>
</div>

{/* Content Tabs */}
<div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
{/* Tabs Navigation */}
<div className="border-b border-gray-200 px-4 sm:px-6 lg:px-8">
<div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
<button
onClick={() => setActiveTab('overview')}
className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
activeTab === 'overview'
? 'border-primary-500 text-primary-600'
: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
}`}
>
Caractéristiques
</button>
<button
onClick={() => setActiveTab('specs')}
className={`py-4  px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
activeTab === 'specs'
? 'border-primary-500 text-primary-600'
: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
}`}
>
Spécifications
</button>
<button
onClick={() => setActiveTab('features')}
className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
activeTab === 'features'
? 'border-primary-500 text-primary-600'
: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
}`}
>
Avantages
</button>
<button
onClick={() => setActiveTab('docs')}
className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
activeTab === 'docs'
? 'border-primary-500 text-primary-600'
: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
}`}
>
Documentation
</button>
</div>
</div>

{/* Tab Content */}
<div className="p-4 sm:p-6 lg:p-8">
{activeTab === 'overview' && (
<div className="space-y-8">
{/* Overview Content */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
<div className="prose max-w-none">
<h3 className="text-xl font-bold text-gray-900 mb-4">La {product.name} en détail</h3>
<p className="text-gray-600 leading-relaxed">{product.description}</p>

<div className="mt-6 bg-primary-50 p-4 rounded-lg">
<h4 className="text-lg font-semibold text-gray-900 mb-3">Points forts</h4>
<ul className="space-y-2">
<li className="flex items-start">
<CheckCircle2 size={18} className="text-primary-500 mr-2 mt-1" />
<span>Autonomie exceptionnelle pour une liberté de mouvement totale</span>
</li>
<li className="flex items-start">
<CheckCircle2 size={18} className="text-primary-500 mr-2 mt-1" />
<span>Moteur puissant et fiable adapté aux conditions africaines</span>
</li>
<li className="flex items-start">
<CheckCircle2 size={18} className="text-primary-500 mr-2 mt-1" />
<span>Coûts d'exploitation réduits par rapport aux motos thermiques</span>
</li>
<li className="flex items-start">
<CheckCircle2 size={18} className="text-primary-500 mr-2 mt-1" />
<span>Connectivité avancée pour un suivi en temps réel</span>
</li>
</ul>
</div>
</div>

{/* Video Section */}
<div id="product-video" className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden relative">
<div className="absolute inset-0 flex items-center justify-center bg-gray-800">
<div className="text-center">
<div className="mb-4">
<button 
onClick={toggleVideo}
className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center text-white hover:bg-primary-700 transition-colors"
>
{isVideoPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
</button>
</div>
<h4 className="text-white font-medium">Découvrez la {product.name} en action</h4>
</div>
{/* Placeholder for actual video */}
<video 
ref={videoRef}
className="absolute inset-0 w-full h-full object-cover opacity-50"
poster={productImages[0]}
onPlay={() => setIsVideoPlaying(true)}
onPause={() => setIsVideoPlaying(false)}
onEnded={() => setIsVideoPlaying(false)}
>
<source src="#" type="video/mp4" />
Votre navigateur ne supporte pas la lecture vidéo.
</video>
</div>
</div>
</div>

{/* Comparison Toggle */}
<div className="border-t border-gray-200 pt-6">
<button
onClick={() => setShowComparisonTable(!showComparisonTable)}
className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
>
<span className="font-medium text-gray-900">Comparer avec d'autres modèles</span>
{showComparisonTable ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
</button>

{showComparisonTable && (
<div className="mt-4 overflow-x-auto">
<table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
<thead className="bg-gray-50">
<tr>
<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caractéristiques</th>
{comparisonData.models.map((model, index) => (
<th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
<div className="flex flex-col">
<span>{model.name}</span>
<span className="text-primary-600 font-bold">{formatCurrency(model.price)}</span>
</div>
</th>
))}
</tr>
</thead>
<tbody className="bg-white divide-y divide-gray-200">
{comparisonData.features.map((feature, index) => (
<tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
<td className="px-4 py-3 text-sm text-gray-900 font-medium">{feature.name}</td>
{feature.values.map((value, idx) => (
<td key={idx} className={`px-4 py-3 text-sm ${idx === 0 ? 'text-primary-600 font-medium' : 'text-gray-700'}`}>
{value}
</td>
))}
</tr>
))}
</tbody>
</table>
</div>
)}
</div>
</div>
)}

{activeTab === 'specs' && (
<div className="space-y-6">
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
<div className="bg-gray-50 p-6 rounded-xl">
<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
<Zap size={20} className="text-primary-500 mr-2" />
Performance
</h3>
<div className="space-y-3">
{Object.entries(specs)
.filter(([key]) => ['autonomie', 'puissance', 'vitesse_max', 'temps_charge'].includes(key))
.map(([key, value]) => (
<div key={key} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-lg">
<span className="text-gray-700 capitalize">{key.replace('_', ' ')}</span>
<span className="font-medium text-gray-900">{value}</span>
</div>
))}
</div>
</div>

<div className="bg-gray-50 p-6 rounded-xl">
<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
<Scale size={20} className="text-primary-500 mr-2" />
Caractéristiques
</h3>
<div className="space-y-3">
{Object.entries(specs)
.filter(([key]) => ['poids', 'charge_max', 'batterie'].includes(key))
.map(([key, value]) => (
<div key={key} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-lg">
<span className="text-gray-700 capitalize">{key.replace('_', ' ')}</span>
<span className="font-medium text-gray-900">{value}</span>
</div>
))}
</div>
</div>

<div className="bg-gray-50 p-6 rounded-xl">
<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
<Tool size={20} className="text-primary-500 mr-2" />
Équipement
</h3>
<div className="space-y-3">
{Object.entries(specs)
.filter(([key]) => ['suspension', 'freins', 'pneus', 'éclairage'].includes(key))
.map(([key, value]) => (
<div key={key} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-lg">
<span className="text-gray-700 capitalize">{key.replace('_', ' ')}</span>
<span className="font-medium text-gray-900">{value}</span>
</div>
))}
</div>
</div>

<div className="bg-gray-50 p-6 rounded-xl">
<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
<Smartphone size={20} className="text-primary-500 mr-2" />
Connectivité
</h3>
<div className="space-y-3">
{Object.entries(specs)
.filter(([key]) => ['affichage', 'connectivité'].includes(key))
.map(([key, value]) => (
<div key={key} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-lg">
<span className="text-gray-700 capitalize">{key.replace('_', ' ')}</span>
<span className="font-medium text-gray-900">{value}</span>
</div>
))}
</div>
</div>
</div>

<div className="flex justify-end">
<button
onClick={handleDownloadSpec}
className="inline-flex items-center px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
>
<Download size={18} className="mr-2" />
Télécharger les spécifications complètes
</button>
</div>
</div>
)}

{activeTab === 'features' && (
<div className="space-y-6">
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
{(specs.avantages as string[])?.map((advantage, index) => (
<div 
key={index}
className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-all duration-300 transform hover:translate-y-[-2px]"
>
<div className="mr-3 mt-0.5">
{advantage.startsWith('🎁') && <Gift size={20} className="text-primary-500" />}
{advantage.startsWith('🛡️') && <Shield size={20} className="text-blue-500" />}
{advantage.startsWith('✨') && <Award size={20} className="text-orange-500" />}
{advantage.startsWith('📝') && <FileCheck size={20} className="text-purple-500" />}
{advantage.startsWith('⛑️') && <HardHat size={20} className="text-red-500" />}
</div>
<div>
<p className="text-gray-900 font-medium">{advantage}</p>
<p className="text-sm text-gray-600 mt-1">
{advantage.startsWith('🎁') && "Commencez à utiliser votre moto sans payer le premier mois et profitez de 7 échanges de batterie gratuits."}
{advantage.startsWith('🛡️') && "Roulez en toute sérénité avec une assurance complète couvrant tous les risques pendant 12 mois."}
{advantage.startsWith('✨') && "Bénéficiez d'une garantie constructeur de 12 mois sur toutes les pièces et la main d'œuvre."}
{advantage.startsWith('📝') && "Nous nous occupons de toutes les démarches administratives pour l'immatriculation de votre moto."}
{advantage.startsWith('⛑️') && "Recevez un casque homologué et un gilet réfléchissant pour assurer votre sécurité sur la route."}
</p>
</div>
</div>
))}
</div>

{/* Promo Section */}
<div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white mt-8">
<div className="flex items-start gap-4">
<div className="p-3 bg-white/20 rounded-xl">
<Percent size={24} className="text-white" />
</div>
<div>
<h3 className="text-xl font-bold mb-2">Offre spéciale lancement</h3>
<p className="mb-4">Pour tout achat avant le 30 juin, bénéficiez de 3 mois d'assurance supplémentaires et d'un kit d'accessoires offert.</p>
<Link 
to="/contact"
className="inline-flex items-center px-4 py-2 bg-white text-primary-700 rounded-lg hover:bg-gray-100 transition-colors"
>
En savoir plus
<ArrowRight size={16} className="ml-2" />
</Link>
</div>
</div>
</div>
</div>
)}

{activeTab === 'docs' && (
<div className="space-y-6">
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<button
onClick={handleDownloadSpec}
className="flex items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all transform hover:translate-y-[-2px]"
>
<Download size={20} className="text-primary-500 mr-3" />
<div className="text-left">
<span className="block font-medium text-gray-900">Fiche Technique</span>
<span className="text-sm text-gray-500">PDF - 2.1 MB</span>
</div>
</button>
<Link
to="/contact"
className="flex items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all transform hover:translate-y-[-2px]"
>
<FileText size={20} className="text-primary-500 mr-3" />
<div className="text-left">
<span className="block font-medium text-gray-900">Manuel Utilisateur</span>
<span className="text-sm text-gray-500">Guide complet</span>
</div>
</Link>
<Link
to="/contact"
className="flex items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all transform hover:translate-y-[-2px]"
>
<Tool size={20} className="text-primary-500 mr-3" />
<div className="text-left">
<span className="block font-medium text-gray-900">Guide Maintenance</span>
<span className="text-sm text-gray-500">Conseils et entretien</span>
</div>
</Link>
</div>

{/* FAQ Section */}
<div className="mt-8">
<h3 className="text-xl font-bold text-gray-900 mb-4">Questions fréquentes</h3>
<div className="space-y-3">
{faqData.map((faq, index) => (
<div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
<button
onClick={() => toggleFaq(index)}
className="flex items-center justify-between w-full p-4 text-left bg-white hover:bg-gray-50 transition-colors"
>
<span className="font-medium text-gray-900">{faq.question}</span>
{expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
</button>
{expandedFaq === index && (
<div className="p-4 bg-gray-50 border-t border-gray-200">
<p className="text-gray-700">{faq.answer}</p>
</div>
)}
</div>
))}
</div>
</div>
</div>
)}
</div>
</div>

{/* Financing Options - Masqué pour Teranga+ */}
{product.id !== 'teranga-plus' && (
<div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
<div className="p-4 sm:p-6 lg:p-8">
<h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Options de financement</h2>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
{/* Pay As You Go */}
<div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 border border-primary-100 hover:border-primary-300 transition-all hover:shadow-lg">
<div className="flex items-start gap-4 mb-4">
<div className="p-3 bg-primary-100 rounded-xl">
<Calendar size={24} className="text-primary-600" />
</div>
<div>
<h3 className="text-lg font-bold text-gray-900 mb-1">Pay As You Go</h3>
<p className="text-sm text-gray-600 mb-4">Paiement flexible à l'utilisation avec premier mois gratuit</p>

<div className="space-y-2 mb-4">
<div className="flex justify-between text-sm">
<span className="text-gray-600">Acompte minimum</span>
<span className="font-medium">150 000 FCFA</span>
</div>
<div className="flex justify-between text-sm">
<span className="text-gray-600">Mensualité à partir de</span>
<span className="font-medium">{formatCurrency(Math.round(product.basePrice / 24))}</span>
</div>
<div className="flex justify-between text-sm">
<span className="text-gray-600">Durée</span>
<span className="font-medium">6 à 24 mois</span>
</div>
</div>

<button
onClick={() => setShowSimulationForm(true)}
className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
>
Simuler mon plan
<ArrowRight size={16} className="ml-2" />
</button>
</div>
</div>
</div>

{/* Pay Molo */}
<div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 border border-primary-100 hover:border-primary-300 transition-all hover:shadow-lg">
<div className="flex items-start gap-4 mb-4">
<div className="p-3 bg-primary-100 rounded-xl">
<CreditCard size={24} className="text-primary-600" />
</div>
<div>
<h3 className="text-lg font-bold text-gray-900 mb-1">Pay Molo</h3>
<p className="text-sm text-gray-600 mb-4">Payez à votre rythme avant de récupérer votre moto</p>

<div className="space-y-2 mb-4">
<div className="flex justify-between text-sm">
<span className="text-gray-600">Acompte minimum</span>
<span className="font-medium">150 000 FCFA</span>
</div>
<div className="flex justify-between text-sm">
<span className="text-gray-600">Paiement</span>
<span className="font-medium">Flexible</span>
</div>
<div className="flex justify-between text-sm">
<span className="text-gray-600">Livraison</span>
<span className="font-medium">Après paiement complet</span>
</div>
</div>

<button
onClick={() => setShowPayMoloForm(true)}
className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
>
Démarrer avec Pay Molo
<ArrowRight size={16} className="ml-2" />
</button>
</div>
</div>
</div>
</div>
</div>
</div>
)}

{/* Related Products */}
<div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-100">
<div className="p-4 sm:p-6 lg:p-8">
<h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Vous pourriez aussi aimer</h2>

<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
{MOTORCYCLE_MODELS.filter(model => model.id !== slug).map((model) => (
<Link 
key={model.id}
to={`/products/${model.id}`}
className="group bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-primary-200 transition-all hover:shadow-lg"
>
<div className="relative h-48 md:h-56 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
{model.image ? (
<img
src={model.image}
alt={`Moto électrique ${model.name}`}
loading="lazy"
width="300"
height="225"
className="w-full h-full object-contain p-4 transition-transform group-hover:scale-110 duration-500"
onError={(e) => {
const target = e.target as HTMLImageElement;
target.style.display = 'none';
const fallback = target.nextElementSibling as HTMLElement;
if (fallback) fallback.style.display = 'flex';
}}
/>
) : null}
{/* Fallback si l'image ne charge pas */}
<div className="flex items-center justify-center h-full" style={{ display: model.image ? 'none' : 'flex' }}>
<div className="text-center">
<div className="text-4xl font-bold text-primary-300 mb-2">{model.name[0]}</div>
<p className="text-sm text-gray-500">Image non disponible</p>
</div>
</div>
<div className="absolute top-2 right-2 bg-white rounded-full shadow-md px-2 py-1 z-10">
<span className="text-primary-600 font-medium text-sm">{formatCurrency(model.basePrice)}</span>
</div>
</div>

<div className="p-4">
<div className="flex justify-between items-center mb-2">
<h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{model.name}</h3>
<span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs">{model.usage}</span>
</div>

<div className="flex items-center gap-4 mb-3">
<div className="flex items-center text-sm text-gray-600">
<Battery size={14} className="text-primary-500 mr-1" />
{model.specs.autonomie}
</div>
<div className="flex items-center text-sm text-gray-600">
<Gauge size={14} className="text-primary-500 mr-1" />
{model.specs.vitesse}
</div>
</div>

<div className="flex items-center justify-between">
<div className="flex">
{[1, 2, 3, 4, 5].map((star) => (
<Star 
key={star} 
size={14} 
className={star <= model.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
/>
))}
</div>
<span className="text-primary-600 group-hover:translate-x-1 transition-transform inline-flex items-center text-sm">
Voir détails
<ChevronRight size={16} className="ml-1" />
</span>
</div>
</div>
</Link>
))}
</div>
</div>
</div>
</div>

{/* Fullscreen Gallery */}
{showFullscreenGallery && (
<div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
<button 
onClick={handleGalleryClose}
className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
>
<X size={20} />
</button>

<button
onClick={handleGalleryPrev}
className="absolute left-2 sm:left-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
>
<ChevronLeft size={20} />
</button>

<div className="w-full h-full flex items-center justify-center p-4 sm:p-8">
<img
src={productImages[galleryIndex]}
alt={`${product.name} - Image ${galleryIndex + 1}`}
className="max-w-full max-h-full object-contain"
/>
</div>

<button
onClick={handleGalleryNext}
className="absolute right-2 sm:right-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
>
<ChevronRight size={20} />
</button>

<div className="absolute bottom-4 sm:bottom-8 left-0 right-0 flex justify-center gap-2">
{productImages.map((_, index) => (
<button
key={index}
onClick={() => setGalleryIndex(index)}
className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
index === galleryIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/50'
}`}
aria-label={`Image ${index + 1}`}
/>
))}
</div>
</div>
)}

{/* Teranga+ Reservation Form */}
<TerangaPlusReservationForm
isOpen={showTerangaPlusReservation}
onClose={() => setShowTerangaPlusReservation(false)}
selectedColor={selectedColor}
/>

{/* Pay Molo Form */}
<PayMoloForm
isOpen={showPayMoloForm}
onClose={() => setShowPayMoloForm(false)}
productName={product.name}
productPrice={product.basePrice}
/>

{/* Simulation Form */}
<SimulationForm
isOpen={showSimulationForm}
onClose={() => setShowSimulationForm(false)}
/>

<style>{`
@keyframes shimmer {
0%, 100% { opacity: 1; }
50% { opacity: 0.7; }
}

.animate-shimmer {
animation: shimmer 2s ease-in-out infinite;
}
`}</style>
</div>
);
};

export default ProductDetail;